/**
 * Timeline constants for the Feature Timeline visualization
 *
 * Centralizes color schemes, segment metadata, and layout configuration
 * for consistent timeline rendering.
 */

import type { ColorScheme, ColorSchemeOption, SegmentInfo, LegendItem, SegmentType } from '@/types/timeline';

/** Default color used when a team is not found in the color map */
export const FALLBACK_COLOR = '#636df1';

/**
 * Due date probability color thresholds
 *
 * Colors follow a traffic light pattern from red (risky) to dark green (safe):
 * - Red: < 50% probability
 * - Orange: 50-70% probability
 * - Yellow: 70-85% probability
 * - Green: 85-95% probability
 * - Dark Green: >= 95% probability
 */
export const PROBABILITY_COLORS = {
  /** Critical risk - less than 50% chance of meeting due date */
  critical: 'var(--color-feedback-error)',
  /** High risk - 50-70% chance */
  high: '#f97316', // orange-500
  /** Medium risk - 70-85% chance */
  medium: '#eab308', // yellow-500
  /** Low risk - 85-95% chance */
  low: '#22c55e', // green-500
  /** On track - 95%+ chance */
  safe: '#15803d', // green-700 (dark green)
} as const;

/** Probability thresholds for color bands */
export const PROBABILITY_THRESHOLDS = {
  critical: 0.5,
  high: 0.7,
  medium: 0.85,
  low: 0.95,
} as const;

/**
 * Get the color for a given probability value (using default PROBABILITY_COLORS)
 * @param probability - Probability value (0-1), or null
 * @returns CSS color string
 * @deprecated Use getProbabilityColorFromScheme for theme-aware colors
 */
export function getProbabilityColor(probability: number | null): string {
  if (probability === null) return PROBABILITY_COLORS.critical;
  if (probability < PROBABILITY_THRESHOLDS.critical) return PROBABILITY_COLORS.critical;
  if (probability < PROBABILITY_THRESHOLDS.high) return PROBABILITY_COLORS.high;
  if (probability < PROBABILITY_THRESHOLDS.medium) return PROBABILITY_COLORS.medium;
  if (probability < PROBABILITY_THRESHOLDS.low) return PROBABILITY_COLORS.low;
  return PROBABILITY_COLORS.safe;
}

/**
 * Get the color for a given probability value using the active color scheme
 * @param probability - Probability value (0-1), or null
 * @param scheme - Active color scheme to use
 * @returns CSS color string from the color scheme
 */
export function getProbabilityColorFromScheme(probability: number | null, scheme: ColorScheme): string {
  if (probability === null) return scheme.activeWork; // critical - use activeWork (red-ish)
  if (probability < PROBABILITY_THRESHOLDS.critical) return scheme.activeWork;
  if (probability < PROBABILITY_THRESHOLDS.high) return scheme.maybeCompletion; // orange
  if (probability < PROBABILITY_THRESHOLDS.medium) return scheme.probableCompletion; // yellow
  if (probability < PROBABILITY_THRESHOLDS.low) return scheme.highProbable; // green
  return scheme.veryHighProbable; // dark green
}

/** Segment info for tooltips and labels */
export const SEGMENT_INFO: Record<SegmentType, SegmentInfo> = {
  maybeStarted: { label: 'Maybe Started', probability: '20% - 50%' },
  activeWork: { label: 'Active Work', probability: '50% started - 50% finished' },
  maybeCompletion: { label: 'Maybe Completion', probability: '50% - 70%' },
  probableCompletion: { label: 'Probable Completion', probability: '70% - 85%' },
  highProbable: { label: 'High Probability', probability: '85% - 95%' },
  veryHighProbable: { label: 'Very High Probability', probability: '95%+' },
};

/** Available color schemes for timeline segments */
export const COLOR_SCHEMES: ColorSchemeOption[] = [
  {
    id: 'sunset',
    name: 'Sunset',
    colors: {
      maybeStarted: '#fda4af',
      activeWork: '#f43f5e',
      maybeCompletion: '#f97316',
      probableCompletion: '#eab308',
      highProbable: '#22c55e',
      veryHighProbable: '#15803d', // dark green
      empty: '#6b7280', // gray-500
    },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: {
      maybeStarted: '#7dd3fc',
      activeWork: '#0284c7',
      maybeCompletion: '#0891b2',
      probableCompletion: '#0d9488',
      highProbable: '#16a34a',
      veryHighProbable: '#166534', // green-800
      empty: '#64748b', // slate-500
    },
  },
  {
    id: 'electric-violet',
    name: 'Electric Violet',
    colors: {
      maybeStarted: '#a78bfa',
      activeWork: '#8b5cf6',
      maybeCompletion: '#3b82f6',
      probableCompletion: '#06b6d4',
      highProbable: '#10b981',
      veryHighProbable: '#047857', // emerald-700
      empty: '#71717a', // zinc-500
    },
  },
  {
    id: 'neon',
    name: 'Neon',
    colors: {
      maybeStarted: '#c4b5fd',
      activeWork: '#a855f7',
      maybeCompletion: '#ec4899',
      probableCompletion: '#f59e0b',
      highProbable: '#22c55e',
      veryHighProbable: '#15803d', // green-700
      empty: '#a1a1aa', // zinc-400
    },
  },
  {
    id: 'brand',
    name: 'Brand',
    colors: {
      maybeStarted: '#c7d6fe',
      activeWork: '#636df1',
      maybeCompletion: '#06b6d4',
      probableCompletion: '#10b981',
      highProbable: '#34d399',
      veryHighProbable: '#059669', // emerald-600
      empty: '#9ca3af', // gray-400
    },
  },
];

/** Default color scheme ID */
export const DEFAULT_COLOR_SCHEME = 'sunset';

/** Legend labels for timeline segments */
export const LEGEND_LABELS: LegendItem[] = [
  { key: 'maybeStarted', label: 'Maybe started (20%-50%)' },
  { key: 'activeWork', label: 'Active work' },
  { key: 'maybeCompletion', label: 'Maybe completion (50-70%)' },
  { key: 'probableCompletion', label: 'Probable completion (70-85%)' },
  { key: 'highProbable', label: 'High probable completion (85-95%)' },
  { key: 'veryHighProbable', label: 'Very high probability (95%+)' },
];

/**
 * Zoom configuration for timeline horizontal scaling
 *
 * Users can zoom in to see details in dense timelines,
 * or zoom out to get an overview of longer timelines.
 */
export const ZOOM_CONFIG = {
  /** Minimum zoom level (100% - no zoom out) */
  MIN: 1.0,
  /** Maximum zoom level (500% - zoomed in) */
  MAX: 5.0,
  /** Default zoom level (100%) */
  DEFAULT: 1.0,
  /** Increment/decrement step for zoom buttons */
  STEP: 0.25,
} as const;

/**
 * Label column configuration for timeline
 *
 * The label column can be resized horizontally to accommodate
 * longer deliverable names or maximize chart area.
 */
export const LABEL_CONFIG = {
  /** Minimum width of label column in pixels */
  MIN_WIDTH: 100,
  /** Maximum width of label column in pixels */
  MAX_WIDTH: 400,
  /** Default width of label column in pixels */
  DEFAULT_WIDTH: 180,
  /** Width of the resize handle in pixels */
  RESIZE_HANDLE_WIDTH: 4,
} as const;

/**
 * Gap connector visual style - frames gaps as OPPORTUNITIES not warnings
 * - "highlight": Soft golden/amber glow with dashed border
 * - "magnetic": Gradient pulling toward edges suggesting blocks want to connect
 */
export const GAP_STYLE: 'highlight' | 'magnetic' = 'magnetic';

/** Opportunity colors for gap connectors (per color scheme) */
export const GAP_OPPORTUNITY_COLORS: Record<string, { fill: string; border: string }> = {
  sunset: { fill: '#FFF7ED', border: '#F59E0B' }, // warm amber
  ocean: { fill: '#F0F9FF', border: '#0EA5E9' }, // sky blue
  'electric-violet': { fill: '#FAF5FF', border: '#A855F7' }, // purple
  neon: { fill: '#FDF4FF', border: '#E879F9' }, // pink/magenta
  brand: { fill: '#EEF2FF', border: '#636DF1' }, // indigo/brand
} as const;

/**
 * Dependency Path Visualization Colors
 *
 * Used when clicking a deliverable to highlight its dependency chain:
 * - Blocking (upstream): Items that must complete BEFORE the selected one
 * - Following (downstream): Items that are WAITING for the selected one
 * - Selected: The clicked deliverable itself
 */
export const DEPENDENCY_PATH_COLORS = {
  /** Blocking/upstream direction - warm amber/orange tones (conveys "wait/caution") */
  blocking: {
    line: '#f59e0b', // amber-500
    background: '#fef3c7', // amber-100
    text: '#b45309', // amber-700
  },
  /** Following/downstream direction - cool blue/cyan tones (conveys "future/flow") */
  following: {
    line: '#0ea5e9', // sky-500
    background: '#e0f2fe', // sky-100
    text: '#0369a1', // sky-700
  },
  /** Selected item - distinct purple highlight */
  selected: {
    line: '#8b5cf6', // violet-500
    background: '#ede9fe', // violet-100
    text: '#6d28d9', // violet-700
  },
} as const;

/**
 * Depth-based opacity levels for dependency path visualization
 *
 * Items closer to the selected deliverable appear more prominent.
 * Array index = depth (0 = selected item, 1 = direct dependency, etc.)
 *
 * Extensible: Add more values to support deeper chains.
 * Currently: 3 levels (direct, 2-hop, 3+ hops)
 */
export const DEPENDENCY_DEPTH_OPACITY = [
  1.0, // depth 0: selected item (not used for dependencies, but included for completeness)
  1.0, // depth 1: direct dependencies (full visibility)
  0.7, // depth 2: 2-hop dependencies (slightly faded)
  0.4, // depth 3+: distant dependencies (more faded)
] as const;

/**
 * Get opacity for a given depth in the dependency chain
 * @param depth - Number of hops from selected item (1 = direct, 2 = 2-hop, etc.)
 * @returns Opacity value (0-1)
 */
export function getDepthOpacity(depth: number): number {
  if (depth <= 0) return DEPENDENCY_DEPTH_OPACITY[0] ?? 1.0;
  if (depth >= DEPENDENCY_DEPTH_OPACITY.length) {
    return DEPENDENCY_DEPTH_OPACITY[DEPENDENCY_DEPTH_OPACITY.length - 1] ?? 0.4;
  }
  return DEPENDENCY_DEPTH_OPACITY[depth] ?? 1.0;
}

/** Layout constants for timeline rendering */
export const TIMELINE_LAYOUT = {
  /** Width of label column in pixels (default, can be overridden) */
  LABEL_WIDTH: 180,
  /** Height of each row in pixels */
  ROW_HEIGHT: 54,
  /** Height of timeline bars in pixels */
  BAR_HEIGHT: 24,
  /** Height of sticky header in pixels */
  HEADER_HEIGHT: 40,
  /** Border radius for rounded-sm bar ends */
  BAR_RADIUS: 4,
  /** Max characters for main row labels (based on LABEL_WIDTH of 180px) */
  MAX_MAIN_LABEL_CHARS: 22,
  /** Max characters for sub-labels */
  MAX_SUB_LABEL_CHARS: 28,
  /** Minimum chart width in pixels */
  MIN_CHART_WIDTH: 400,
  /** Buffer days to add after max date */
  DATE_BUFFER_DAYS_END: 4,
  /** Minimum spacing between month labels in pixels */
  MIN_LABEL_SPACING: 80,
  /** Top padding for chart body to prevent due date badges from being clipped */
  CHART_TOP_PADDING: 24,
  /** Height of separator between groups in pixels */
  GROUP_SEPARATOR_HEIGHT: 8,
  /** Width of left accent bar on group items in pixels */
  GROUP_ACCENT_WIDTH: 4,
  /** Opacity of left accent bar on group items (0-1) */
  GROUP_ACCENT_OPACITY: 0.5,
  /** Opacity of group header left accent bar (0-1) */
  GROUP_HEADER_ACCENT_OPACITY: 1.0,
} as const;

/** Get a color scheme by ID, with fallback to first scheme */
export function getColorScheme(schemeId: string): ColorScheme {
  const scheme = COLOR_SCHEMES.find((s) => s.id === schemeId);
  if (scheme) return scheme.colors;
  const fallback = COLOR_SCHEMES[0];
  if (fallback) return fallback.colors;
  // Should never happen, but satisfies TypeScript
  return {
    maybeStarted: '#ccc',
    activeWork: '#999',
    maybeCompletion: '#666',
    probableCompletion: '#444',
    highProbable: '#222',
    veryHighProbable: '#111',
    empty: '#888',
  };
}
